import { ChangeDetectionStrategy, Component, forwardRef, input } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { LucideCheck, LucideMinus } from '@lucide/angular';
import { FieldBase } from '../shared/field-base';
import { FieldErrorComponent } from '../shared/field-error.component';

@Component({
  selector: 'ds-checkbox',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet, FieldErrorComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
  template: `
    <div class="ds-field">
      <label
        class="check"
        [class.check--checked]="value() || indeterminate()"
        [class.check--disabled]="disabled()"
      >
        <input
          class="check__input"
          type="checkbox"
          [id]="fieldId"
          [checked]="value() ?? false"
          [indeterminate]="indeterminate()"
          [disabled]="disabled()"
          [required]="required()"
          [attr.aria-invalid]="errorText() ? 'true' : null"
          [attr.aria-describedby]="describedBy()"
          (change)="toggle($event)"
          (blur)="onTouched()"
        />
        <span class="check__box" aria-hidden="true">
          @if (indeterminate()) {
            <ng-container
              [ngComponentOutlet]="minusIcon"
              [ngComponentOutletInputs]="{ size: 20, strokeWidth: 3.5 }"
            />
          } @else if (value()) {
            <ng-container
              [ngComponentOutlet]="checkIcon"
              [ngComponentOutletInputs]="{ size: 20, strokeWidth: 3.5 }"
            />
          }
        </span>
        <span class="check__text">
          <span class="check__label">
            {{ label() }}
            @if (required()) {
              <span class="ds-field__required" aria-hidden="true">*</span>
            }
          </span>
          @if (helperText()) {
            <span class="ds-field__helper check__helper" [id]="fieldId + '-helper'">
              {{ helperText() }}
            </span>
          }
        </span>
      </label>
      @if (errorText()) {
        <ds-field-error [errorId]="fieldId + '-error'">{{ errorText() }}</ds-field-error>
      }
    </div>
  `,
  styles: `
    .check {
      display: flex;
      align-items: flex-start;
      gap: var(--space-3);
      min-height: var(--touch-target);
      padding: var(--space-2) 0;
      cursor: pointer;

      &--disabled {
        cursor: not-allowed;

        .check__label,
        .check__helper {
          color: var(--color-text-disabled);
        }
      }
    }

    .check__input {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      clip-path: inset(50%);
      overflow: hidden;
    }

    .check__box {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      margin-top: 1px;
      border: 2.5px solid var(--color-border-input);
      border-radius: var(--radius-sm);
      background: var(--color-surface);
      color: var(--color-text-on-primary);
      transition: background-color var(--motion-fast), border-color var(--motion-fast);
    }

    .check--checked .check__box {
      background: var(--color-primary);
      border-color: var(--color-primary);
    }

    .check--disabled .check__box {
      background: var(--color-surface-sunken);
      border-color: var(--color-border);
      color: var(--color-text-disabled);
    }

    .check__input:focus-visible + .check__box {
      outline: var(--focus-ring);
      outline-offset: var(--focus-offset);
    }

    .check__text {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .check__label {
      font-size: var(--text-md);
      color: var(--color-text);
    }

    .check__helper {
      margin: 0;
    }
  `,
})
export class CheckboxComponent extends FieldBase<boolean> {
  indeterminate = input(false);

  protected readonly checkIcon = LucideCheck;
  protected readonly minusIcon = LucideMinus;

  protected toggle(event: Event): void {
    const next = (event.target as HTMLInputElement).checked;
    this.value.set(next);
    this.onChange(next);
  }
}
